import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import apiService from "../services/api";
import TourHelpButton from "../components/TourHelpButton";
import ZoomableImage from "../components/ZoomableImage";
import OptionalLabel from "../components/OptionalLabel";
import { useProductTour } from "../onboarding/useProductTour";
import { TOUR_IDS } from "../onboarding/tours";
import analytics, { errorCode } from "../services/analytics";
import "../styles.css";

const isQueueEndError = (err) =>
  err.response?.status === 404 ||
  err.response?.data?.error?.includes("unannotated");

const AnnotatePage = () => {
  const [currentImage, setCurrentImage] = useState(null);
  const [nextImage, setNextImage] = useState(null);
  const [genus, setGenus] = useState("");
  const [species, setSpecies] = useState("");
  const [availableSpecies, setAvailableSpecies] = useState([]);
  const [allSpeciesData, setAllSpeciesData] = useState(null);
  const [gender, setGender] = useState("unknown");
  const [explanation, setExplanation] = useState("");
  const [other, setOther] = useState(""); // "" | "Multiple Mosquitoes" | "Partial Mosquito" | "No Mosquitoes"
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [queueError, setQueueError] = useState("");
  const [retryCursor, setRetryCursor] = useState(undefined);
  const [sessionExhausted, setSessionExhausted] = useState(false);
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const generationRef = useRef(0);
  const initializedUserIdRef = useRef(null);
  const prefetchPromiseRef = useRef(null);
  const preloadedImageRef = useRef(null);
  const trackedSessionRef = useRef(null);
  const trackedImageRef = useRef(null);
  const trackedExhaustedRef = useRef(false);
  const navigate = useNavigate();
  const { isTourRunning, startTour } = useProductTour({
    tourId: TOUR_IDS.annotation,
    userId: user?.id,
    ready: Boolean(user) && !isLoading && Boolean(currentImage),
  });

  const requestNextImage = useCallback(async (afterId) => {
    const started = performance.now();
    try {
      const data = await apiService.getNextUnannotatedImage(afterId);
      analytics.capture("queue_request_completed", {
        duration_ms: Math.round(performance.now() - started),
        status: data.mosquitoImage ? "success" : "empty",
      });
      return data.mosquitoImage || null;
    } catch (err) {
      analytics.capture("queue_request_completed", {
        duration_ms: Math.round(performance.now() - started),
        status: isQueueEndError(err) ? "empty" : "failed",
        ...(isQueueEndError(err) ? {} : { error_code: errorCode(err) }),
      });
      if (isQueueEndError(err)) return null;
      throw err;
    }
  }, []);

  const loadStats = useCallback(async (generation = generationRef.current) => {
    setIsStatsLoading(true);
    try {
      const statsData = await apiService.getUserStats();
      if (generation === generationRef.current) setStats(statsData.stats);
    } catch (err) {
      console.error("Failed to load annotation stats:", err);
    } finally {
      if (generation === generationRef.current) setIsStatsLoading(false);
    }
  }, []);

  const prefetchNext = useCallback((afterId, generation) => {
    if (!afterId || prefetchPromiseRef.current) return prefetchPromiseRef.current;

    const request = requestNextImage(afterId)
      .then((image) => {
        if (generation !== generationRef.current) return undefined;

        if (!image) {
          setSessionExhausted(true);
          return null;
        }

        setNextImage(image);
        const url = image.signedUrl || image.gcsUrl;
        if (url && typeof Image !== "undefined") {
          const preloader = new Image();
          preloader.src = url;
          preloadedImageRef.current = preloader;
        }
        return image;
      })
      .catch((err) => {
        console.error("Failed to prefetch the next image:", err);
        if (generation === generationRef.current) {
          setQueueError("The next image could not be prefetched. We will retry when you continue.");
        }
        return undefined;
      })
      .finally(() => {
        if (prefetchPromiseRef.current === request) {
          prefetchPromiseRef.current = null;
        }
      });

    prefetchPromiseRef.current = request;
    return request;
  }, [requestNextImage]);

  const loadCurrentAfter = useCallback(async (afterId, isInitial = false) => {
    const generation = generationRef.current;
    setIsLoading(true);
    setQueueError("");
    setRetryCursor(afterId);

    try {
      const image = await requestNextImage(afterId);
      if (generation !== generationRef.current) return;

      setCurrentImage(image);
      setNextImage(null);
      setSessionExhausted(!image && !isInitial);
      setRetryCursor(undefined);

      if (image) void prefetchNext(image.id, generation);
    } catch (err) {
      console.error("Failed to load annotation image:", err);
      if (generation === generationRef.current) {
        setCurrentImage(null);
        setQueueError("Failed to load the next image. Please try again.");
      }
    } finally {
      if (generation === generationRef.current) setIsLoading(false);
    }
  }, [prefetchNext, requestNextImage]);

  const loadData = useCallback((profile) => {
    const generation = ++generationRef.current;
    setUser(profile);
    setCurrentImage(null);
    setNextImage(null);
    setSessionExhausted(false);
    setQueueError("");
    prefetchPromiseRef.current = null;
    preloadedImageRef.current = null;

    void loadCurrentAfter(undefined, true);
    void loadStats(generation);
    apiService
      .getSpeciesList()
      .then((speciesData) => {
        if (generation === generationRef.current) setAllSpeciesData(speciesData);
      })
      .catch((err) => {
        console.error("Failed to load species:", err);
        if (generation === generationRef.current) {
          setError("Failed to load the species list. Please refresh and try again.");
        }
      });
  }, [loadCurrentAfter, loadStats]);

  useEffect(() => {
    const unsubscribe = apiService.onAuthChange((profile) => {
      if (!apiService.hasResolved()) return;
      if (!profile) {
        navigate("/auth");
        return;
      }
      if (profile.role !== "EXPERT") {
        navigate("/pending");
        return;
      }
      setUser(profile);
      if (initializedUserIdRef.current === profile.id) return;
      initializedUserIdRef.current = profile.id;
      loadData(profile);
    });
    return () => {
      generationRef.current += 1;
      initializedUserIdRef.current = null;
      unsubscribe();
    };
  }, [navigate, loadData]);

  useEffect(() => {
    if (!user || trackedSessionRef.current === user.id) return;
    trackedSessionRef.current = user.id;
    analytics.capture("annotation_session_started");
  }, [user]);

  useEffect(() => {
    if (!currentImage || trackedImageRef.current === currentImage.id) return;
    trackedImageRef.current = currentImage.id;
    analytics.capture("annotation_image_displayed");
  }, [currentImage]);

  useEffect(() => {
    if (user && !isLoading && !currentImage && !queueError && sessionExhausted && !trackedExhaustedRef.current) {
      trackedExhaustedRef.current = true;
      analytics.capture("annotation_session_exhausted");
    }
  }, [user, isLoading, currentImage, queueError, sessionExhausted]);

  const handleGenusChange = (e) => {
    const selectedGenus = e.target.value;
    setGenus(selectedGenus);
    setSpecies(""); // Reset species when genus changes
    if (selectedGenus) setOther(""); // Picking a normal annotation clears any Other selection

    // Update available species based on selected genus
    if (selectedGenus && selectedGenus !== "unknown" && allSpeciesData?.speciesByGenus) {
      setAvailableSpecies(allSpeciesData.speciesByGenus[selectedGenus] || []);
    } else {
      setAvailableSpecies([]);
    }
  };

  // Every click clears the annotation fields, including Explanation.
  // Clicking the active option again still deselects it for normal annotation.
  const handleOtherChange = (value) => {
    setOther((previous) => previous === value ? "" : value);
    setGenus("");
    setSpecies("");
    setAvailableSpecies([]);
    setGender("unknown");
    setExplanation("");
  };

  const resetForm = useCallback(() => {
    setGenus("");
    setSpecies("");
    setAvailableSpecies([]);
    setGender("unknown");
    setExplanation("");
    setOther("");
    setError("");
  }, []);

  const advanceToNext = useCallback(async () => {
    if (!currentImage) return;

    const cursor = currentImage.id;
    let upcoming = nextImage;
    setQueueError("");
    setNextImage(null);
    preloadedImageRef.current = null;

    if (!upcoming && !sessionExhausted) {
      const pendingPrefetch = prefetchPromiseRef.current;
      upcoming = pendingPrefetch ? await pendingPrefetch : undefined;

      if (upcoming === undefined) {
        try {
          upcoming = await requestNextImage(cursor);
        } catch (err) {
          console.error("Failed to advance the annotation queue:", err);
          setCurrentImage(null);
          setRetryCursor(cursor);
          setQueueError("Failed to load the next image. Please try again.");
          return;
        }
      }
    }

    setNextImage(null);
    if (!upcoming) {
      setCurrentImage(null);
      setSessionExhausted(true);
      return;
    }

    setCurrentImage(upcoming);
    setSessionExhausted(false);
    setRetryCursor(undefined);
    void prefetchNext(upcoming.id, generationRef.current);
  }, [currentImage, nextImage, prefetchNext, requestNextImage, sessionExhausted]);

  const handleSubmitAnnotation = async () => {
    if (!genus && !species && !other) {
      setError("Please select at least genus or species, or pick one of the Other options");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const result = await apiService.createAnnotation(
        currentImage.id,
        genus === "unknown" ? undefined : genus,
        species === "unknown" ? undefined : species,
        gender,
        explanation || undefined,
        other || undefined
      );
      analytics.capture("annotation_submitted", {
        annotation_type: other ? "other" : "taxonomy",
        consensus_reached: Boolean(result?.consensusReached),
        image_other_locked: Boolean(result?.imageOtherLocked),
      });
      resetForm();
      await advanceToNext();
      void loadStats();
    } catch (err) {
      analytics.capture("annotation_failed", { error_code: errorCode(err) });
      // If the image was already flagged for consensus by another user, silently move on
      if (err.response?.data?.error === 'Annotation complete') {
        resetForm();
        await advanceToNext();
        void loadStats();
        return;
      }
      setError(err.response?.data?.error || err.response?.data?.message || "Failed to submit annotation");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (isAdvancing || isSubmitting) return;
    analytics.capture("annotation_skipped");
    setIsAdvancing(true);
    resetForm();
    try {
      await advanceToNext();
    } finally {
      setIsAdvancing(false);
    }
  };

  const handleEndSession = () => {
    analytics.capture("annotation_session_ended", { reason: "end_session_button" });
    navigate("/dashboard");
  };

  const handleLogout = async () => {
    await apiService.logout();
    navigate("/");
  };

  // Removed old annotation type dropdown handlers - now using direct genus/species input

  if (isLoading || (!currentImage && isStatsLoading && !queueError && !sessionExhausted)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading...</div>
      </div>
    );
  }

  if (!currentImage) {
    // Scenario 1: the org's annotation queue is empty because no non-DNA images
    // exist for it yet (totalImages already filters out DNA-barcoded captures).
    // Scenario 3: there ARE non-DNA images, but this user has annotated them all.
    const orgHasNoImages = (stats?.totalImages || 0) === 0;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <nav className="p-6 flex justify-between items-center bg-black/30 backdrop-blur-sm">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors text-sm"
            >
              ← Dashboard
            </button>
            <h1 className="text-2xl font-bold text-white">Digitomy Trap Annotate</h1>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
          >
            Logout
          </button>
        </nav>
        <div className="flex flex-col items-center justify-center h-[80vh] text-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-12 max-w-md"
          >
            {queueError ? (
              <>
                <div className="text-6xl mb-4">⚠️</div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  Image loading paused
                </h2>
                <p className="text-gray-300 text-lg mb-6">{queueError}</p>
              </>
            ) : sessionExhausted ? (
              <>
                <div className="text-6xl mb-4">✅</div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  Session Complete
                </h2>
                <p className="text-gray-300 text-lg mb-6">
                  You've reached the end of the available images for this session.
                  Skipped images may appear again when you start a new session.
                </p>
              </>
            ) : orgHasNoImages ? (
              <>
                <div className="text-6xl mb-4">📭</div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  Nothing to annotate yet
                </h2>
                <p className="text-gray-300 text-lg mb-6">
                  There are no mosquito images available yet for annotation
                  from your organization's captured trap data. Please check
                  back later for new image availability.
                </p>
              </>
            ) : (
              <>
                <div className="text-6xl mb-4">🎉</div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  All Caught Up!
                </h2>
                <p className="text-gray-300 text-lg mb-6">
                  You've annotated all available images. Great work!
                </p>
                <p className="text-gray-400 mb-6">
                  You've completed {stats?.annotatedByUser || 0} out of{" "}
                  {stats?.totalImages || 0} images!
                </p>
              </>
            )}
            {queueError ? (
              <button
                onClick={() => loadCurrentAfter(retryCursor, retryCursor === undefined)}
                className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-lg font-semibold transition-all"
              >
                Try Again
              </button>
            ) : (
              <button
                onClick={() => navigate("/dashboard")}
                className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-lg font-semibold transition-all"
              >
                Back to Dashboard
              </button>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <nav className="p-4 sm:p-6 flex flex-wrap gap-4 justify-between items-center bg-black/30 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/dashboard")}
            className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors text-sm"
          >
            ← Dashboard
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Digitomy Trap Annotate</h1>
            {user?.username && (
              <p className="text-gray-300 text-sm">{user.username}</p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-right" data-tour="annotation-progress">
            <p className="text-gray-300 text-sm">Your Progress</p>
            <p className="text-white text-xl font-bold">
              {stats?.annotatedByUser || 0} / {stats?.totalImages || 0}
            </p>
            <p className="text-indigo-300 text-xs">
              {stats?.userProgress || 0}% complete
            </p>
          </div>
          <TourHelpButton onClick={startTour} isRunning={isTourRunning} />
          <button
            onClick={handleEndSession}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
          >
            End Session
          </button>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
          >
            Logout
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {queueError && (
          <div
            role="status"
            className="mb-6 rounded-lg border border-amber-400/50 bg-amber-500/15 p-3 text-amber-100"
          >
            {queueError}
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Image Display */}
          <motion.div
            key={currentImage.id}
            data-tour="annotation-image"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex min-h-[360px] flex-col rounded-2xl border border-white/20 bg-white/10 p-6 backdrop-blur-lg"
          >
            <ZoomableImage
              src={currentImage.signedUrl || currentImage.gcsUrl}
              alt="Mosquito specimen"
              title="Mosquito Image"
              controlsTourId="annotation-zoom"
              className="min-h-0 flex-1"
            />
          </motion.div>

          {/* Annotation Form */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <h2 className="text-2xl font-bold text-white mb-6">Annotation</h2>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200"
              >
                {error}
              </motion.div>
            )}

            <div data-tour="annotation-taxonomy">
            {/* Genus Dropdown */}
            <div className="mb-6">
              <label className="block text-white text-sm font-medium mb-3">
                Genus *
              </label>
              <select
                value={genus}
                onChange={handleGenusChange}
                disabled={!!other}
                className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white focus:outline-none focus:ring-2 focus:ring-blue-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ color: genus ? 'white' : '#9ca3af' }}
              >
                <option value="" disabled style={{ backgroundColor: '#1e293b', color: '#9ca3af' }}>
                  Select genus...
                </option>
                <option value="unknown" style={{ backgroundColor: '#1e293b', color: 'white' }}>
                  Unknown
                </option>
                {allSpeciesData?.genera?.map((g) => (
                  <option key={g} value={g} style={{ backgroundColor: '#1e293b', color: 'white' }}>
                    {g}
                  </option>
                ))}
              </select>
            </div>

            {/* Species Dropdown (only shown when genus is selected) */}
            <div className="mb-6">
              <label className="block text-white text-sm font-medium mb-3">
                Species *
              </label>
              <select
                value={species}
                onChange={(e) => setSpecies(e.target.value)}
                disabled={!!other || !genus || genus === "unknown"}
                className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white focus:outline-none focus:ring-2 focus:ring-blue-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ color: species ? 'white' : '#9ca3af' }}
              >
                <option value="" disabled style={{ backgroundColor: '#1e293b', color: '#9ca3af' }}>
                  {!genus || genus === "unknown" ? "Select genus first..." : "Select species..."}
                </option>
                {genus && genus !== "unknown" && (
                  <>
                    <option value="unknown" style={{ backgroundColor: '#1e293b', color: 'white' }}>
                      Unknown
                    </option>
                    {availableSpecies.map((s) => (
                      <option key={s} value={s} style={{ backgroundColor: '#1e293b', color: 'white' }}>
                        {s}
                      </option>
                    ))}
                  </>
                )}
              </select>
              {genus && genus !== "unknown" && availableSpecies.length === 0 && (
                <p className="text-yellow-300 text-xs mt-1">No species available for this genus</p>
              )}
            </div>
            </div>

            {/* Gender Selection */}
            <div className="mb-6" data-tour="annotation-gender">
              <label className="block text-white text-sm font-medium mb-3">
                Gender *
              </label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                disabled={!!other}
                className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="unknown" className="bg-slate-800">Unknown</option>
                <option value="male" className="bg-slate-800">Male</option>
                <option value="female" className="bg-slate-800">Female</option>
              </select>
            </div>

            {/* Explanation */}
            <div className="mb-6" data-tour="annotation-notes">
              <label htmlFor="annotation-explanation" className="block text-white text-sm font-medium mb-2">
                Explanation <OptionalLabel />
              </label>
              <textarea
                id="annotation-explanation"
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                disabled={!!other}
                placeholder="Add any notes or observations..."
                rows={4}
                className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* "Other" block — highlighted. Picking any of these locks the
                image for everyone else and clears genus/species. */}
            <div
              className="mb-6 p-4 rounded-xl bg-amber-500/15 border-2 border-amber-400/60 shadow-[0_0_24px_-8px_rgba(251,191,36,0.6)]"
              data-tour="annotation-other"
            >
              <label className="block text-amber-100 text-sm font-semibold mb-3">
                <OptionalLabel />{" "}
                Choose one of these options if the image does not contain a single intact mosquito:
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {["Multiple Mosquitoes", "Partial Mosquito", "No Mosquitoes"].map((opt) => {
                  const active = other === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => handleOtherChange(opt)}
                      className={
                        "py-2 px-3 rounded-lg text-sm font-medium transition-all border " +
                        (active
                          ? "bg-amber-400 text-slate-900 border-amber-300 shadow-md"
                          : "bg-white/10 text-amber-50 border-amber-300/40 hover:bg-amber-400/20")
                      }
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4" data-tour="annotation-actions">
              <button
                onClick={handleSkip}
                disabled={isSubmitting || isAdvancing}
                className="flex-1 py-3 px-6 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-all border border-white/30 disabled:opacity-50"
              >
                {isAdvancing ? "Loading next..." : "Skip"}
              </button>
              <button
                onClick={handleSubmitAnnotation}
                disabled={isSubmitting || isAdvancing || (!genus && !species && !other)}
                className="flex-2 py-3 px-8 rounded-lg bg-blue-700 hover:bg-blue-800 text-white font-semibold transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Submitting..." : "Submit Annotation"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnotatePage;
